import { parseUnits } from "viem";
import { api, errMsg } from "./api";
import { NATIVE } from "./swap";

export const ZERO = "0x0000000000000000000000000000000000000000";
const apiAddr = (t) => (t.address === NATIVE ? ZERO : t.address);

export async function getApiQuote({ tokenIn, tokenOut, amountIn, slippage, wallet }) {
  const amt = parseUnits(amountIn, tokenIn.decimals);
  if (amt <= 0n) return null;
  const { data } = await api.post("/swap/quote", { token_in: apiAddr(tokenIn), token_out: apiAddr(tokenOut), amount: amt.toString(), slippage, wallet });
  const s = data.summary;
  return {
    source: "api", raw: data, amountIn: amt, amountOut: BigInt(s.amount_out), minOut: BigInt(s.min_out),
    priceImpact: Number(s.price_impact ?? 0), gasWei: BigInt(s.gas_fee_wei || 0), route: s.route, at: Date.now(),
    hasHooks: s.route.some((r) => r.hooks?.length), fees: s.fees || [], feeTerms: s.fee_terms, grossOut: BigInt(s.gross_out || s.amount_out),
  };
}

// Lets HIVEVORA credit creators from the confirmed receipt; the backend re-reads the chain, so this is best-effort.
const recordSwapFees = async (hash) => {
  for (let i = 0; i < 5; i++) {
    try { await api.post("/swap/confirm", { tx_hash: hash }); return; }
    catch (e) { if (e?.response?.status !== 409) return; await new Promise((r) => setTimeout(r, 3000)); }
  }
};

export const apiRouteLabel = (q) => q.route.map((r) => `${r.protocol}: ${r.path.join(" → ")}`).join(" | ") || "Uniswap";

// Executes approval (if required) → Permit2 signature (if required) → swap, all signed in the user's wallet.
export async function executeApiSwap({ tokenIn, tokenOut, amountIn, slippage, account, pc, setTx, sendTransactionAsync, signTypedDataAsync, runTx, sourceToken }) {
  if (tokenIn.address !== NATIVE) {
    const { data } = await api.post("/swap/check-approval", { wallet: account, token: tokenIn.address, amount: parseUnits(amountIn, tokenIn.decimals).toString() });
    if (data.approval) {
      const a = data.approval;
      const ok = await runTx({ pc, setTx, label: `Approve ${tokenIn.symbol} for Permit2`, send: () => sendTransactionAsync({ chainId: 4663, to: a.to, data: a.data, value: BigInt(a.value || 0) }) });
      if (!ok) return null;
    }
  }
  setTx({ stage: "awaiting_signature", label: "Refreshing quote" });
  let fresh;
  try { fresh = await getApiQuote({ tokenIn, tokenOut, amountIn, slippage, wallet: account }); }
  catch (e) { setTx({ stage: "error", label: "Quote", error: errMsg(e) }); return null; }
  let signature;
  const permit = fresh.raw.permitData;
  if (permit) {
    try {
      setTx({ stage: "awaiting_signature", label: "Sign Permit2 approval (no gas)" });
      signature = await signTypedDataAsync({ domain: permit.domain, types: permit.types, primaryType: permit.primaryType || Object.keys(permit.types).find((k) => k !== "EIP712Domain"), message: permit.values });
    } catch (e) { setTx({ stage: /reject|denied/i.test(e?.message || "") ? "rejected" : "error", label: "Permit2 signature", error: e.shortMessage }); return null; }
  }
  let tx;
  try { tx = (await api.post("/swap/transaction", { quote: fresh.raw.quote, permit_data: permit || null, signature: signature || null, source_token: sourceToken || null })).data.swap; }
  catch (e) { setTx({ stage: "error", label: "Build swap", error: errMsg(e) }); return null; }
  if (tx.from?.toLowerCase() !== account.toLowerCase() || tx.chainId !== 4663) { setTx({ stage: "error", label: "Swap", error: "Unexpected transaction returned — aborted" }); return null; }
  const rc = await runTx({ pc, setTx, label: "Swap", send: () => sendTransactionAsync({ chainId: 4663, to: tx.to, data: tx.data, value: BigInt(tx.value || 0), gas: tx.gasLimit ? BigInt(tx.gasLimit) : undefined }) });
  if (rc && (fresh.fees.length || sourceToken)) recordSwapFees(rc.transactionHash);
  return rc;
}
